import React from 'react';
import { cn } from './cn';

/**
 * Cards or table, for the same data.
 *
 * Two genuinely different jobs, which is why both exist rather than one being
 * the "better" layout: cards are for scanning a handful of things and reading
 * each one, a table is for comparing many of them on one column. Neither wins
 * at the other's job, and an org with fifteen accounts wants the table for the
 * same reason an org with three wants the cards.
 *
 * Icon-only, because the labels ("Cards", "Table") cost more width than the
 * control is worth on a phone and the shapes are unambiguous. The accessible
 * names carry the meaning instead.
 *
 * Segmented rather than a single toggling button: a lone button has to show
 * either the current state or the action, and whichever you pick, half the
 * people read it as the other one.
 */
const CARDS_AND_TABLE = [
  {
    value: 'cards',
    label: 'Card view',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="9" y="9" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    value: 'table',
    label: 'Table view',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M1.5 3.5h13M1.5 8h13M1.5 12.5h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
];

/**
 * `options` defaults to cards/table, so every existing call site is unchanged.
 * Passing your own keeps the one segmented control rather than growing a
 * second that looks almost the same -- the release checklist's "Areas /
 * Order" is the same job with different words.
 *
 * An option carries an `icon` OR a `text`. Text is right where the choice is
 * not a shape anyone recognises: "Areas" and "Order" as glyphs would be two
 * icons nobody can tell apart, and a title attribute explains nothing on a
 * touch screen.
 */
export const ViewToggle = ({
  value, onChange, className, options = CARDS_AND_TABLE, label = 'View', ...props
}) => (
  <div
    role="group"
    aria-label={label}
    className={cn('inline-flex shrink-0 rounded-pill border border-line bg-surface p-0.5', className)}
    {...props}
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={active}
          aria-label={opt.label}
          title={opt.label}
          className={cn(
            // Square and at the tap-target floor. The (pointer: coarse) rule
            // gives height to every button, but an icon-only one also needs
            // the WIDTH or it is a 44px-tall sliver. A text option sets its
            // width from the label instead and only needs the floor.
            'inline-flex items-center justify-center rounded-pill',
            opt.icon ? 'size-[38px]' : 'h-[38px] px-3 text-caption',
            'transition-colors duration-[120ms] [&>svg]:size-4',
            active ? 'bg-surface-raised text-ink' : 'text-ink-subtle hover:text-ink'
          )}
        >
          {opt.icon || opt.text}
        </button>
      );
    })}
  </div>
);

export default ViewToggle;
